// Portal "MY WORK" — mock payloads (spec Part A.10).
//
// These shapes are VERIFIED against live Supabase (2026-10-02 — Epic 0,
// scripts/_probe-portal-signedin.js, 7/7 shape checks passed) and are the build
// target for every Portal component. Counts drift as the team works; the
// SHAPES must not. See docs/PORTAL-MY-WORK-EPICS.md "Backend verification".
//
// `src/api/portal.js` returns these behind REACT_APP_PORTAL_STUB=1 (Part H
// step 1) so the whole page builds with no database. No component imports this
// file directly — only `api/portal.js` does (the single data-access surface,
// Epic 1 acceptance criterion: "No Portal component imports supabase
// directly").
//
// Reference parks (Part A.3 preamble):
//   COTA  — work in every state (the happy path).
//   VERDE — nothing in Portal (the empty state).
//   RIVER SANDS — a second park, for the all-parks operator view.

export const COTA = '8c344570-5cab-4930-b212-4fc27f1ad935';
export const VERDE = 'cd94da9a-42af-4c73-8b6a-467469e23cbe';
export const RIVER_SANDS = 'a1b2c3d4-1111-2222-3333-444455556666';

// Part A.3 item shape — exact key set verified live:
//   id, park, when, drill, stage, steps, title, holder, done_at, park_id,
//   asked_at, asked_by, stage_key, asked_label, stage_label, holder_label
const item = (over = {}) => ({
  id: 'item-1',
  park: 'COTA RV Park',
  park_id: COTA,
  when: '2026-10-02',
  title: 'Rate sheet for October weekend',
  stage: 'review',
  stage_key: 'review',
  stage_label: 'In review',
  holder: 'Erin Forrest',
  holder_label: 'Erin (CRR)',
  steps: [
    { key: 'intake', label: 'Intake', state: 'done' },
    { key: 'draft', label: 'Draft', state: 'done' },
    { key: 'review', label: 'In review', state: 'current' },
    { key: 'deliver', label: 'Deliver', state: 'todo' },
  ],
  drill: { fn: 'fn_portal_drill', value: 'item' },
  done_at: null,
  asked_at: null,
  asked_by: null,
  asked_label: null,
  ...over,
});

// ── A.3 — the whole page (fn_get_portal) ─────────────────────────────────────
// Panels always in order: waiting_on_you, tracker, conversation, done_recently,
// team, history (verified live — the order is binding).
export const page = {
  status: 'ok',
  can_write: true,
  as_of: 'Today',
  date_range: 'live',
  parks: [{ client_id: COTA, name: 'COTA RV Park' }],
  verdict:
    'Two items were recently delivered and one is waiting on you — the October rate sheet is in review and needs your sign-off.',
  panels: [
    {
      panel: 'waiting_on_you',
      facts: {
        count: 5,
        items: [
          item({ id: 'w1', title: 'October rate sheet — sign-off', stage: 'review', stage_label: 'In review' }),
          item({ id: 'w2', title: 'Site map review for new loop', stage: 'draft', stage_label: 'Drafting' }),
          item({ id: 'w3', title: 'Confirm pet policy update', stage: 'intake', stage_label: 'Intake' }),
          item({ id: 'w4', title: 'Approve seasonal banner copy', stage: 'review', stage_label: 'In review' }),
          item({ id: 'w5', title: 'Verify cancellation rules', stage: 'draft', stage_label: 'Drafting' }),
        ],
      },
    },
    {
      panel: 'tracker',
      facts: {
        groups: [
          { label: 'Pricing', items: [
            item({ id: 't1', title: 'Rate sheet refresh', stage: 'review' }),
            item({ id: 't2', title: 'Tiered pricing for holidays', stage: 'draft' }),
          ] },
          { label: 'Operations', items: [
            item({ id: 't3', title: 'Site map for new loop', stage: 'draft' }),
            item({ id: 't4', title: 'Pet policy update', stage: 'intake' }),
          ] },
        ],
      },
    },
    {
      panel: 'conversation',
      facts: {
        unread: 2,
        latest: { id: 'm1', from: 'Erin (CRR)', preview: 'Pushed the revised rate sheet — take a look when you can.', when: '2026-10-02' },
      },
    },
    {
      panel: 'done_recently',
      facts: {
        items: [
          item({ id: 'd1', title: 'September rate sheet', stage: 'deliver', stage_label: 'Delivered', done_at: '2026-09-28' }),
          item({ id: 'd2', title: 'Loop A signage refresh', stage: 'deliver', stage_label: 'Delivered', done_at: '2026-09-25' }),
        ],
      },
    },
    {
      panel: 'team',
      facts: {
        members: [
          { id: 'u1', name: 'Erin Forrest', label: 'Erin (CRR)', role: 'Account lead', open: 3 },
          { id: 'u2', name: 'Marcus Lee', label: 'Marcus', role: 'Ops', open: 2 },
          { id: 'u3', name: 'Priya Nair', label: 'Priya', role: 'Pricing', open: 1 },
        ],
      },
    },
    { panel: 'history', facts: { total: 16, asked_for: 5 } },
  ],
};


// fn_get_portal(null) — the all-parks operator view (15 parks, 8 tracker groups
// per spec Part J; mock carries 2 parks so the layout is observable).
export const pageAllParks = {
  ...page,
  parks: [
    { client_id: COTA, name: 'COTA RV Park' },
    { client_id: RIVER_SANDS, name: 'River Sands RV' },
  ],
  verdict:
    'Across your parks, four items are waiting on clients and two trackers moved since yesterday.',
  panels: page.panels.map((p) =>
    p.panel === 'tracker'
      ? {
          ...p,
          facts: {
            groups: [
              ...p.facts.groups,
              { label: 'Reservations', items: [item({ id: 't5', park: 'River Sands RV', park_id: RIVER_SANDS, title: 'Min-stay rules for holidays' })] },
            ],
          },
        }
      : p,
  ),
};

// fn_get_portal(VERDE) — the empty park (0 tracker groups, empty verdict +
// Ask CTA per Part A.3 / Epic 5).
export const pageEmpty = {
  status: 'ok',
  can_write: true,
  as_of: 'Today',
  date_range: 'live',
  parks: [{ client_id: VERDE, name: 'Verde Ranch' }],
  verdict: '',
  panels: [
    { panel: 'waiting_on_you', facts: { count: 0, items: [] } },
    { panel: 'tracker', facts: { groups: [] } },
    { panel: 'conversation', facts: { unread: 0, latest: null } },
    { panel: 'done_recently', facts: { items: [] } },
    { panel: 'team', facts: { members: [] } },
    { panel: 'history', facts: { total: 0, asked_for: 0 } },
  ],
};


// ── A.4 — drillItem (fn_portal_drill, p_what='item') ─────────────────────────
// kind:'item', one row with timeline[], messages[], reply{fn,value,can_write}.
export const drillItem = {
  kind: 'item',
  label: 'October rate sheet — sign-off',
  rows: [
    {
      ...item({ id: 'w1' }),
      timeline: [
        { key: 'ev1', label: 'Intake logged', when: '2026-09-28', who: 'Erin (CRR)' },
        { key: 'ev2', label: 'Draft shared', when: '2026-09-29', who: 'Marcus' },
        { key: 'ev3', label: 'Revision pushed', when: '2026-10-01', who: 'Erin (CRR)' },
        { key: 'ev4', label: 'Awaiting your sign-off', when: '2026-10-02', who: null },
      ],
      messages: [],
      reply: { fn: 'fn_portal_send', value: 'send', can_write: true },
    },
  ],
};

// A.4 variant — an item whose drill already carries the threaded messages
// (Part A.4 messages[] shape), used by ItemDetail when the conversation is
// inlined.
export const drillItemWithMessages = {
  ...drillItem,
  rows: [
    {
      ...drillItem.rows[0],
      messages: [
        { id: 'm1', from: 'Erin (CRR)', body: 'Pushed the revised rate sheet — take a look when you can.', when: '2026-10-02', mine: false },
        { id: 'm2', from: 'You', body: 'Looks good — one note on the holiday tier.', when: '2026-10-02', mine: true },
      ],
    },
  ],
};

// A.4 — drillPerson (p_what='person'). A team-member drill: kind 'person',
// rows are the items that member holds.
export const drillPerson = {
  kind: 'person',
  label: 'Erin (CRR)',
  rows: [
    item({ id: 'w1', title: 'October rate sheet — sign-off', holder: 'Erin Forrest', holder_label: 'Erin (CRR)' }),
    item({ id: 't1', title: 'Rate sheet refresh', holder: 'Erin Forrest', holder_label: 'Erin (CRR)' }),
  ],
};

// A.4 — drillTracker (p_what='tracker'). A tracker group drill.
export const drillTracker = {
  kind: 'tracker',
  label: 'Pricing',
  rows: [
    item({ id: 't1', title: 'Rate sheet refresh' }),
    item({ id: 't2', title: 'Tiered pricing for holidays' }),
  ],
};

// A.4 — drillHistory (p_what='history'). The list drill: kind 'history' with
// total + offset (paginated), rows open an item (drill.value='item').
export const drillHistory = {
  kind: 'history',
  total: 16,
  offset: 0,
  rows: [
    item({ id: 'h1', title: 'September rate sheet', stage: 'deliver', stage_label: 'Delivered', done_at: '2026-09-28' }),
    item({ id: 'h2', title: 'Loop A signage refresh', stage: 'deliver', stage_label: 'Delivered', done_at: '2026-09-25' }),
  ],
};

// A.4 — drillDone (p_what='done_recently').
export const drillDone = {
  kind: 'done_recently',
  total: 2,
  offset: 0,
  rows: [
    item({ id: 'd1', title: 'September rate sheet', stage: 'deliver', stage_label: 'Delivered', done_at: '2026-09-28' }),
    item({ id: 'd2', title: 'Loop A signage refresh', stage: 'deliver', stage_label: 'Delivered', done_at: '2026-09-25' }),
  ],
};

// ── A.5 — olderMessages (fn_portal_conversation) ─────────────────────────────
// { park_id, messages[] }. 0 today per Part J (mock carries prior messages so
// the "load older" interaction is observable).
export const olderMessages = {
  park_id: COTA,
  messages: [
    { id: 'm0', from: 'Marcus', body: 'Drafting the site map now.', when: '2026-09-29', mine: false },
    { id: 'm1', from: 'Erin (CRR)', body: 'Pushed the revised rate sheet.', when: '2026-10-02', mine: false },
  ],
};

// ── Write results (fn_portal_send / fn_portal_request / fn_portal_mark_read) ──
// Writes return a small result envelope; the UI optimistically updates and the
// Realtime `message`/`work` event reconciles (Part A.8).
export const sendResult = { ok: true, message_id: 'm-new', when: '2026-10-02' };
export const askResult = { ok: true, request_id: 'r-new', when: '2026-10-02' };
export const markReadResult = { ok: true };

export default {
  page,
  pageAllParks,
  pageEmpty,
  drillItem,
  drillItemWithMessages,
  drillPerson,
  drillTracker,
  drillHistory,
  drillDone,
  olderMessages,
  sendResult,
  askResult,
  markReadResult,
};

